import { useMutation } from '@tanstack/react-query';
import React, { forwardRef, useImperativeHandle, useState } from 'react';
import styled from 'styled-components';

import { Button, Input } from '@column/column-ui-kit';

import {
  type PendingPasskeyRegistration,
  beginPasskeyRegistration,
  finishPasskeyRegistration,
  getDefaultPasskeyName,
} from '~/lib/webauthn';
import { MFAStatus } from '~/repositories';
import { useNotificationStore } from '~/stores/Notification';
import { useSessionStore } from '~/stores/Session';
import { Box } from '~/styles';

const PasskeySetupTransition = styled(Box)<{ $closeImmediately: boolean; $visible: boolean }>`
  display: grid;
  grid-template-rows: ${({ $visible }) => ($visible ? '1fr' : '0fr')};
  opacity: ${({ $visible }) => ($visible ? 1 : 0)};
  overflow: hidden;
  pointer-events: ${({ $visible }) => ($visible ? 'auto' : 'none')};
  visibility: ${({ $visible }) => ($visible ? 'visible' : 'hidden')};
  width: 100%;
  transition:
    grid-template-rows ${({ $closeImmediately }) => ($closeImmediately ? '0ms' : '300ms')} ease,
    opacity ${({ $closeImmediately }) => ($closeImmediately ? '0ms' : '200ms')} ease,
    visibility 0s linear ${({ $closeImmediately, $visible }) => ($visible || $closeImmediately ? '0s' : '300ms')};

  > div {
    min-height: 0;
    overflow: hidden;
  }
`;

const PasskeySetupHeader = styled.div`
  padding: 18px 24px 8px;
  text-align: left;

  h2 {
    color: ${({ theme }) => theme.foreground};
    font-size: 14px;
    font-weight: 500;
    line-height: 20px;
    margin: 0 0 4px;
  }

  p {
    color: ${({ theme }) => theme.secondary.background};
    font-size: 14px;
    line-height: 20px;
    margin: 0;
  }
`;

const NamePasskeyField = styled.div`
  align-items: stretch;
  display: flex;
  gap: 8px;
  padding: 8px 24px 24px;
`;

const PasskeyNameInput = styled(Input)`
  flex: 1;
  min-width: 0;
`;

const AddPasskeyButton = styled(Button)`
  align-self: stretch;
`;

export interface SetupPasskeyFlowHandle {
  reset: () => void;
  start: () => void;
}

interface SetupPasskeyFlowProps {
  notificationDisplay: string;
  onFailure: () => void;
  onSuccess: () => void;
}

export const SetupPasskeyFlow = forwardRef<SetupPasskeyFlowHandle, SetupPasskeyFlowProps>(
  ({ notificationDisplay, onFailure, onSuccess }, ref) => {
    const { addDangerNotification, addSuccessNotification } = useNotificationStore();
    const firstName = useSessionStore((state) => state.currentUser?.firstName ?? '');
    const setMfaStatus = useSessionStore((state) => state.setMfaStatus);
    const [closeImmediately, setCloseImmediately] = useState(false);
    const [isComplete, setIsComplete] = useState(false);
    const [isOpen, setIsOpen] = useState(false);
    const [displayName, setDisplayName] = useState('');
    const [pendingRegistration, setPendingRegistration] = useState<PendingPasskeyRegistration>();

    const resetRegistration = (immediate = false) => {
      setCloseImmediately(immediate);
      setIsComplete(false);
      setIsOpen(false);
      setDisplayName('');
      setPendingRegistration(undefined);
    };

    const beginRegistration = useMutation({
      mutationFn: beginPasskeyRegistration,
      onSuccess: (registration) => {
        setCloseImmediately(false);
        setIsComplete(false);
        setPendingRegistration(registration);
        setDisplayName(getDefaultPasskeyName(firstName));
        setIsOpen(true);
      },
      onError: (error: unknown) => {
        onFailure();
        if (error instanceof DOMException && error.name === 'NotAllowedError') {
          return;
        }
        if (error instanceof DOMException && error.name === 'InvalidStateError') {
          addDangerNotification({
            content: 'A passkey has already been registered to this device',
            display: notificationDisplay,
          });
          return;
        }
        const content = error instanceof Error ? error.message : 'Failed to add passkey. Please try again.';
        addDangerNotification({ content, display: notificationDisplay });
      },
    });

    const finishRegistration = useMutation({
      mutationFn: ({ name, registration }: { name: string; registration: PendingPasskeyRegistration }) =>
        finishPasskeyRegistration({ displayName: name, registration }),
      onSuccess: () => {
        setIsComplete(true);
        addSuccessNotification({ content: 'Passkey added', display: notificationDisplay });
        onSuccess();
        setMfaStatus(MFAStatus.VERIFIED);
      },
      onError: (error: unknown) => {
        const content = error instanceof Error ? error.message : 'Failed to add passkey. Please try again.';
        addDangerNotification({ content, display: notificationDisplay });
      },
    });

    useImperativeHandle(ref, () => ({
      reset: () => resetRegistration(true),
      start: () => {
        if (!beginRegistration.isPending) {
          beginRegistration.mutate();
        }
      },
    }));

    return (
      <PasskeySetupTransition
        $closeImmediately={closeImmediately}
        $visible={isOpen}
        aria-hidden={!isOpen}
        variant="secondary"
      >
        <div>
          <form
            onSubmit={(event) => {
              event.stopPropagation();
              event.preventDefault();
              const name = displayName.trim();
              if (name && pendingRegistration) {
                finishRegistration.mutate({ name, registration: pendingRegistration });
              }
            }}
          >
            <PasskeySetupHeader>
              <h2>Name your passkey</h2>
              <p>Use a name that helps you recognize this key</p>
            </PasskeySetupHeader>
            <NamePasskeyField>
              <PasskeyNameInput
                value={displayName}
                onChange={setDisplayName}
                placeholder="e.g. Work laptop"
                maxLength={127}
                isDisabled={isComplete}
              />
              <AddPasskeyButton
                type="submit"
                size="small"
                isDisabled={isComplete || !displayName.trim() || !pendingRegistration}
                isLoading={finishRegistration.isPending}
              >
                Add passkey
              </AddPasskeyButton>
            </NamePasskeyField>
          </form>
        </div>
      </PasskeySetupTransition>
    );
  }
);

SetupPasskeyFlow.displayName = 'SetupPasskeyFlow';
